import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMail, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { ApiHandler } from "../../helper/ApiHandler";
import illustration from "../../assest/Forgot-password-bro.png";
import SEO from "../../utils/Seo";

const FORGOT_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .fp-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .fp-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes fp-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .fp-float { animation: fp-float 4.5s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .fp-float { animation: none !important; }
  }
`;

const ForgotPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        const body = {
            email: email,
        };

        try {
            const result = await ApiHandler(
                "/forgot_password.php",
                "POST",
                body,
                undefined,
                dispatch,
                navigate
            );

            if (result.data.status === "1") {
                toast.success(result.data.msg);
                navigate("/");
            } else {
                toast.error(result.data.msg);
                setEmail("");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Reset Password - MyAd360"
                description="Reset your MyAd360 dashboard login password securely."
            />
            <style>{FORGOT_STYLES}</style>

            <div
                className="fp-body min-h-screen flex items-center justify-center px-4 py-8"
                style={{
                    background:
                        "radial-gradient(90% 70% at 15% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 100% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
                }}
            >
                <div
                    className="max-w-6xl w-full bg-white shadow-[0_35px_90px_-28px_rgba(28,20,16,0.3)] overflow-hidden grid grid-cols-1 md:grid-cols-2 border-2 border-[#FDE8CD] relative"
                    style={{ borderRadius: "1rem 3rem 1rem 3rem" }}
                >
                    {/* accent corner ribbon */}
                    <div
                        aria-hidden="true"
                        className="hidden md:block absolute top-0 right-0 w-24 h-24 pointer-events-none"
                        style={{
                            background: "linear-gradient(135deg, transparent 50%, #FDE8CD 50%)",
                            borderRadius: "0 1rem 0 0",
                        }}
                    />
                    {/* Left: Illustration */}
                    <div
                        className="hidden md:flex items-center justify-center p-6 relative overflow-hidden"
                        style={{
                            background:
                                "radial-gradient(120% 100% at 30% 20%, #FEF3E5 0%, #FFF4E5 60%, #FFFBF5 100%)",
                        }}
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -top-10 -left-10 w-56 h-56 rounded-full opacity-30 blur-3xl"
                            style={{ background: "radial-gradient(circle, #F59E0B, transparent 70%)" }}
                        />
                        <div
                            aria-hidden="true"
                            className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full opacity-25 blur-3xl"
                            style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
                        />
                        <img
                            src={illustration}
                            alt="Forgot Password Illustration"
                            className="fp-float max-h-[400px] w-auto relative z-10"
                        />
                    </div>

                    {/* Right: Form */}
                    <div className="p-8 sm:p-12">
                        <h2
                            className="text-3xl font-bold text-center text-[#1C1410]"
                            style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
                        >
                            Forgot{" "}
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]">
                                Password
                            </span>
                        </h2>
                        <p className="text-center text-[#8A7560] mb-6 text-sm">
                            No worries! Enter your email and we'll send you a link to reset your password.
                        </p>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-5">
                                <label htmlFor="email" className="block mb-1 text-sm font-medium text-[#3D2E22]">
                                    Email
                                </label>
                                <div className="relative">
                                    <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        placeholder="you@example.com"
                                        className="w-full h-12 pl-11 pr-4 bg-white border border-[#F0DCC8] rounded-xl text-sm focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-all"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white py-3 rounded-xl text-sm font-bold shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 disabled:opacity-70"
                            >
                                {isSubmitting ? "Sending..." : "Submit"}
                                {!isSubmitting && (
                                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                )}
                            </button>
                        </form>

                        <div className="text-center mt-6">
                            <button
                                className="group inline-flex items-center gap-1.5 text-[#D97706] text-sm font-semibold hover:text-[#B45309] transition-colors"
                                onClick={() => navigate("/login")}
                            >
                                <FiArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;